<script lang="ts" setup>
import { ref, onMounted, reactive, getCurrentInstance } from 'vue';
import { useRouter } from 'vue-router';
import { type AppInitializationDto, AuthenticationState, AuthProviderV2 } from '@mit/aws-vue';
import ConfirmDialog from 'primevue/confirmdialog';
import CustomToast from './common/components/CustomToast.vue';
import AppHeader from './common/components/AppHeader.vue';
import { useTextStore } from './stores/textStore';
import { STAGE_CONFIG } from './config';
import { useTravelStore } from './stores/travelStore';
import { usePersonalInformationStore } from './stores/personalInformationStore';
import { Profile } from './api/PersonalInformationApi';

const customToastRef = ref();
const instance = getCurrentInstance();
const router = useRouter();

const navigateToHome = () => {
    sessionStorage.removeItem('selectedProgramTab');
    router.push('/overview');
};

onMounted(() => {
    if (instance && customToastRef.value) {
        instance.appContext.config.globalProperties.$customToast = customToastRef.value;
    }
});

const initializeDto = reactive<AppInitializationDto>({
    isLoading: true,
    hasAccess: false,
    hasError: false
});

let isLoggedIn = ref(false);

// Instantiate the auth provider
const textsStore = useTextStore();
const travelStore = useTravelStore();
const piStore = usePersonalInformationStore();
const profile = ref<Profile | null>(null);

onMounted(async () => {
    try {
        await AuthProviderV2.configure(STAGE_CONFIG);

        const state = AuthProviderV2.getState()
        console.log('state', state)
        if (state === AuthenticationState.ERROR) {
            console.error('Error configuring auth');
            return
        } else if (state === AuthenticationState.UNAUTHENTICATED) {
            await AuthProviderV2.signIn()
        }

        // Initialize user authorization after authentication is complete
        if (state === AuthenticationState.AUTHENTICATED) {
            textsStore.fetchTexts().catch(error => {
                console.warn('Text fetch failed:', error);
            })

            travelStore.fetchRole().catch(error => {
                console.warn('Roles fetch failed:', error);
            }).finally(() => {
                sessionStorage.setItem('adminRole', JSON.stringify(travelStore.adminRole));
            });

            piStore.getProfile().catch(error => {
                console.warn('Profile fetch failed:', error);
            });

            isLoggedIn.value = true;
            initializeDto.isLoading = false;
            initializeDto.hasAccess = true;
            initializeDto.hasError = false;
        }
    } catch (error) {
        console.error('Error initializing auth provider:', error);
        initializeDto.hasError = true;
        initializeDto.hasAccess = false;
        initializeDto.isLoading = false;
        isLoggedIn.value = false;
    }
});
</script>


<template>
    <ConfirmDialog />
    <CustomToast ref="customToastRef" />

    <div v-if="initializeDto.hasError" class="error-screen">
        <h2>Error Initializing Application</h2>
        <p>There was an error during application initialization. Please try again later.</p>
    </div>
    <div v-else-if="isLoggedIn" class="app-container">
        <!-- MIT Logo and Travel Admin Heading -->
        <AppHeader :title="textsStore.getText('admin.title', 'Travel Admin')" :name="profile?.firstName ?? ''"
            @navigate-home="navigateToHome" />

        <!-- Main content -->
        <router-view />
    </div>
</template>

<style scoped>
.loading-screen {
    position: fixed;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100vh;
    width: 100vw;
    background: white;
    z-index: 9999;
}

.app-container {
    max-width: 1440px;
    padding: 0 64px 16px;
    margin: 0 auto;
}

.large-spinner {
    border: 12px solid #f3f3f3;
    border-top: 12px solid #1966FF;
    border-radius: 50%;
    width: 96px;
    height: 96px;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}
</style>
